/**
 * Product Grid Component
 *
 * Displays a grid of products with optional filtering and pagination.
 * Supports standalone filter components via URL params and custom events.
 */

import { useState, useEffect, useCallback } from 'react';
import { productsApi, type ProductsParams } from '@/api/client';
import { useShop } from '@/context';
import { ProductCard } from './ProductCard';
import { Button } from '../shared/Button';
import type { Product, ProductCategory } from '@/types';
import styles from './ProductGrid.module.css';

interface ProductGridProps {
  columns?: number;
  perPage?: number;
  category?: string;
  orderby?: string;
  order?: string;
  ids?: string;
  onSale?: boolean;
  showFilters?: boolean;
}

// Read filter values from URL params
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    category: params.get('category') || '',
    orderby: params.get('orderby') || '',
    order: params.get('order') || '',
    page: parseInt(params.get('page') || '1', 10),
  };
};

export function ProductGrid({
  columns = 3,
  perPage = 12,
  category: initialCategory = '',
  orderby: initialOrderby = 'date',
  order: initialOrder = 'DESC',
  ids: _ids = '',
  onSale = false,
  showFilters = true,
}: ProductGridProps) {
  // ids is reserved for future use (specific product IDs filtering)
  void _ids;

  const { t } = useShop();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get initial values from URL or props
  const urlParams = getUrlParams();

  // Pagination
  const [page, setPage] = useState(urlParams.page);
  const [totalPages, setTotalPages] = useState(1);

  // Filters - URL params take precedence over props
  const [category, setCategory] = useState(urlParams.category || initialCategory);
  const [orderby, setOrderby] = useState(urlParams.orderby || initialOrderby);
  const [order, setOrder] = useState(urlParams.order || initialOrder);

  // Listen for filter change events from standalone filter components
  useEffect(() => {
    const handleFilterChange = (e: CustomEvent<{
      category?: string;
      orderby?: string;
      order?: string;
    }>) => {
      const { category: newCategory, orderby: newOrderby, order: newOrder } = e.detail;

      if (newCategory !== undefined) {
        setCategory(newCategory);
        setPage(1);
      }
      if (newOrderby !== undefined) {
        setOrderby(newOrderby);
        setPage(1);
      }
      if (newOrder !== undefined) {
        setOrder(newOrder);
        setPage(1);
      }
    };

    window.addEventListener('evcs:filter:change', handleFilterChange as EventListener);
    return () => {
      window.removeEventListener('evcs:filter:change', handleFilterChange as EventListener);
    };
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params: ProductsParams = {
          page,
          per_page: perPage,
          orderby: orderby as ProductsParams['orderby'],
          order: order as ProductsParams['order'],
        };

        if (category) params.category = category;
        if (onSale) params.on_sale = true;

        const response = await productsApi.getProducts(params);
        setProducts(response.items || []);
        setTotalPages(response.pages || 1);
      } catch (err) {
        setError('Erreur lors du chargement des produits');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [page, perPage, category, orderby, order, onSale]);

  // Fetch categories (only if showing filters)
  useEffect(() => {
    if (!showFilters) return;

    const fetchCategories = async () => {
      try {
        const response = await productsApi.getCategories();
        const cats = Array.isArray(response)
          ? response
          : ((response as { items?: ProductCategory[] })?.items || []);
        setCategories(cats);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };

    fetchCategories();
  }, [showFilters]);

  const handleOrderChange = useCallback((value: string) => {
    const [newOrderby, newOrder] = value.split('-');
    setOrderby(newOrderby);
    setOrder(newOrder);
    setPage(1);

    // Update URL for consistency with standalone filters
    const url = new URL(window.location.href);
    url.searchParams.set('orderby', newOrderby);
    url.searchParams.set('order', newOrder);
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url.toString());
  }, []);

  const handleCategoryChange = useCallback((value: string) => {
    setCategory(value);
    setPage(1);

    // Update URL for consistency with standalone filters
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set('category', value);
    } else {
      url.searchParams.delete('category');
    }
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url.toString());
  }, []);

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <Button variant="outline" onClick={() => setPage(1)}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Filters - only shown if showFilters is true */}
      {showFilters && (
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label htmlFor="category-filter">Catégorie</label>
            <select
              id="category-filter"
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.slug}>
                  {cat.name} ({cat.count})
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label htmlFor="sort-filter">Trier par</label>
            <select
              id="sort-filter"
              value={`${orderby}-${order}`}
              onChange={(e) => handleOrderChange(e.target.value)}
            >
              <option value="date-DESC">Plus récents</option>
              <option value="date-ASC">Plus anciens</option>
              <option value="price-ASC">Prix croissant</option>
              <option value="price-DESC">Prix décroissant</option>
              <option value="title-ASC">Nom A-Z</option>
              <option value="title-DESC">Nom Z-A</option>
            </select>
          </div>
        </div>
      )}

      {/* Loading Skeleton */}
      {isLoading && (
        <div
          className={styles.grid}
          style={{ '--columns': columns } as React.CSSProperties}
        >
          {Array.from({ length: perPage }).map((_, i) => (
            <div key={i} className={styles.skeleton}>
              <div className={styles.skeletonImage} />
              <div className={styles.skeletonContent}>
                <div className={styles.skeletonTitle} />
                <div className={styles.skeletonPrice} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Products Grid */}
      {!isLoading && products.length === 0 && (
        <div className={styles.empty}>
          <p>Aucun produit trouvé.</p>
        </div>
      )}

      {!isLoading && products.length > 0 && (
        <>
          <div
            className={styles.grid}
            style={{ '--columns': columns } as React.CSSProperties}
          >
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="outline"
                size="small"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Précédent
              </Button>

              <span className={styles.pageInfo}>
                Page {page} sur {totalPages}
              </span>

              <Button
                variant="outline"
                size="small"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Suivant
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
